import { usePayrollStaffCategories } from "hooks/usePayrollStaffCategories"
import { useEffect, useMemo, useState } from "react"
import { Skeleton } from "components/new-stuff/ui/Skeleton"
import {
  PayrollJournalSkeleton,
  PayrollJournalSummarySkeleton,
} from "../components/PayrollSkeletons"
import { useLocation, useParams, useSearchParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import {
  Button as AntdButton,
  Input,
  Popover,
  Select,
  Tooltip,
  message,
} from "antd"
import type { TablePaginationConfig } from "antd/es/table"
import type { SorterResult } from "antd/es/table/interface"
import {
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  SwatchIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline"
import { saveAs } from "file-saver"
import { DashboardContainer } from "components"
import EnhancedReportTable from "components/new-stuff/report-table/EnhancedReportTable"
import type { ReportColumn } from "components/new-stuff/report-table/types"
import ActionsButton from "components/new-stuff/ui/ActionsButton"
import { mixPanelEvent } from "components/new-stuff/mix-panel/MixPanel"
import { newArchAxiosInstance } from "utils/request"
import {
  formatReportDate,
  isReportDateField,
} from "pages/dashboard/documents/reports/utils/report-date"
import {
  parseExcludedPayrollElements,
  serializeExcludedPayrollElements,
} from "./payrollSummaryElementFilters"
import type { ExcludedPayrollElements } from "./payrollSummaryElementFilters"

type SortDirection = "asc" | "desc"
type PayrollReportSort = { field: string; direction: SortDirection }
type DownloadFormat = "excel" | "pdf"
type FilterKey =
  | "fullName"
  | "identificationNumber"
  | "department"
  | "paygrade"
  | "location"
  | "currency"

const FILTER_KEYS: FilterKey[] = [
  "fullName",
  "identificationNumber",
  "department",
  "paygrade",
  "location",
  "currency",
]

const readOptionalParam = (params: URLSearchParams, key: string) =>
  params.get(key) || undefined

const readSorts = (params: URLSearchParams): PayrollReportSort[] => {
  const fields = (params.get("sortBy") || "").split(",").filter(Boolean)
  const directions = (params.get("sortDirection") || "").split(",")
  return fields.map((field, index) => ({
    field,
    direction: directions[index] === "desc" ? "desc" : "asc",
  }))
}

interface PayrollReportRow {
  person_id?: number
  full_name?: string
  identification_number?: string
  employee_title?: string
  department?: string
  paygrade?: string
  location?: string
  hire_date?: string
  currency?: string
  group?: string
  people_count?: number
  total_amount?: number
  total_tax?: number
  total_deducted?: number
  person_columns: Record<string, string | null>
  payroll_columns: Record<string, number>
  payroll_section_columns: Record<string, Record<string, number>>
  payroll_tax_columns: Record<string, number>
  earning_items: Array<{
    description: string
    amount: number
    tax_amount: number
  }>
}

interface ColumnSection {
  key: string
  title: string
  columns: string[]
}

interface PaginatedEmployees {
  docs: PayrollReportRow[]
  page: number
  limit: number
  totalDocs: number
  totalPages: number
}

interface PayrollReportResponse {
  report: {
    payrollStatusId?: number
    report_download_title?: string
    report_date?: string
  }
  columnSections: ColumnSection[]
  compactColumnSections: ColumnSection[]
  employees: PaginatedEmployees
  summarySections: Array<{
    key: string
    title: string
    availableElements?: string[]
    items: Array<{
      description: string
      peopleCount: number
      totalAmount: number
    }>
    totalAmount?: number
    currencyTotals?: Array<{ currency: string; totalAmount: number }>
  }>
  summary?: {
    name: string
    reportDate: string
    title: string
    employmentType?: string
    sections: PayrollReportResponse["summarySections"]
    totalEarnings: number
    totalDeductions: number
    netTotal: number
    currencyTotals?: Array<{
      currency: string
      totalEarnings: number
      totalDeductions: number
      netTotal: number
    }>
  }
  filterOptions: {
    fullNames: string[]
    identificationNumbers: string[]
    departments: string[]
    paygrades: string[]
    locations: string[]
    currencies: string[]
  }
}

type SummarySection = PayrollReportResponse["summarySections"][number]

interface DetailedSectionItem {
  description: string
  amount: number
  taxAmount?: number
  isTotal?: boolean
}

interface DetailedReportRow {
  key: string
  person: PayrollReportRow
  sections: Record<string, DetailedSectionItem[]>
}

interface SectionColors {
  EmployeeDetails: string
  Earnings: string
  Deductions: string
  Summary: string
  EmployerContributions: string
  InformationAmortization: string
}

const DEFAULT_COLORS: SectionColors = {
  EmployeeDetails: "#3F5FBF",
  Earnings: "#23875A",
  Deductions: "#C83E4D",
  Summary: "#5B5FC7",
  EmployerContributions: "#3578B8",
  InformationAmortization: "#95652F",
}

const COLOR_LABELS: Record<keyof SectionColors, string> = {
  EmployeeDetails: "Employee details",
  Earnings: "Earnings",
  Deductions: "Deductions",
  Summary: "Summary",
  EmployerContributions: "Employer contributions",
  InformationAmortization: "Information / Amortization",
}

const sectionColorKey = (section: ColumnSection): keyof SectionColors => {
  const value = `${section.key} ${section.title}`.toLowerCase()
  if (value.includes("summary")) return "Summary"
  if (value.includes("deduction")) return "Deductions"
  if (value.includes("contribution")) return "EmployerContributions"
  if (value.includes("information") || value.includes("amort"))
    return "InformationAmortization"
  if (value.includes("earning")) return "Earnings"
  return "EmployeeDetails"
}

const EXPANDED_VIEW_TOTAL_COLUMNS: Record<string, Set<string>> = {
  Earnings: new Set(["total earnings"]),
  EmployerContributions: new Set(["total contribution"]),
  InformationAmortization: new Set(["total amortization"]),
}

const isExpandedViewItem = (section: ColumnSection, description: string) =>
  !EXPANDED_VIEW_TOTAL_COLUMNS[sectionColorKey(section)]?.has(
    description.trim().toLowerCase()
  )

const getRowValue = (
  row: PayrollReportRow,
  key: string,
  sectionKey?: string
): string | number | null | undefined => {
  if (key in row.person_columns) return row.person_columns[key]
  const sectionValue = sectionKey
    ? row.payroll_section_columns?.[sectionKey]?.[key]
    : undefined
  if (sectionValue !== undefined) return sectionValue
  if (key in row.payroll_columns) return row.payroll_columns[key]
  if (key.endsWith("::tax"))
    return row.payroll_tax_columns?.[key.replace("::tax", "")]
  return row[key as keyof PayrollReportRow] as string | number | undefined
}

const displayValue = (value: string | number | null | undefined) => {
  if (typeof value === "number")
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  return value || "—"
}

const hasMeaningfulValue = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return false
  if (typeof value !== "string") return true

  const normalized = value.trim().toUpperCase()
  return normalized !== "" && normalized !== "NA" && normalized !== "N/A"
}

const displayReportValue = (
  key: string,
  value: string | number | null | undefined
) =>
  typeof value === "string" && isReportDateField(key)
    ? formatReportDate(value)
    : displayValue(value)

const PaginatedPayrollReportDetails = () => {
  const { pathname } = useLocation()
  const { payrollId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const view =
    (searchParams.get("format") as "compact" | "detailed" | "summary") ||
    "compact"
  const preview = searchParams.get("type") === "preview"
  const basePath = `/admin/payrolls/view`

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const initialSearch = searchParams.get("search") || ""
  const [search, setSearch] = useState(initialSearch)
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch)
  const [sorts, setSorts] = useState<PayrollReportSort[]>(() =>
    readSorts(searchParams)
  )
  const sortBy = sorts.map((sort) => sort.field).join(",") || undefined
  const sortDirection =
    sorts.map((sort) => sort.direction).join(",") || undefined
  const [fullName, setFullName] = useState<string | undefined>(() =>
    readOptionalParam(searchParams, "fullName")
  )
  const [identificationNumber, setIdentificationNumber] = useState<
    string | undefined
  >(() => readOptionalParam(searchParams, "identificationNumber"))
  const [department, setDepartment] = useState<string | undefined>(() =>
    readOptionalParam(searchParams, "department")
  )
  const [paygrade, setPaygrade] = useState<string | undefined>(() =>
    readOptionalParam(searchParams, "paygrade")
  )
  const [location, setLocation] = useState<string | undefined>(() =>
    readOptionalParam(searchParams, "location")
  )
  const [currency, setCurrency] = useState<string | undefined>(() =>
    readOptionalParam(searchParams, "currency")
  )
  const staffCategories = usePayrollStaffCategories()
  const [staffCategory, setStaffCategory] = useState<string | undefined>(() =>
    readOptionalParam(searchParams, "staffCategory")
  )
  const [groupBy, setGroupBy] = useState<string | undefined>(() =>
    readOptionalParam(searchParams, "groupBy")
  )
  const [sectionOrder, setSectionOrder] = useState<string[]>(() =>
    (searchParams.get("sectionOrder") || "").split(",").filter(Boolean)
  )
  const [hiddenSections, setHiddenSections] = useState<string[]>(() =>
    (searchParams.get("hiddenSections") || "").split(",").filter(Boolean)
  )
  const [excludedElements, setExcludedElements] =
    useState<ExcludedPayrollElements>(() =>
      parseExcludedPayrollElements(searchParams.get("excludedElements"))
    )
  const serializedExcludedElements = useMemo(
    () => serializeExcludedPayrollElements(excludedElements),
    [excludedElements]
  )
  const [colors, setColors] = useState<SectionColors>(DEFAULT_COLORS)
  const [downloading, setDownloading] = useState<DownloadFormat>()
  const activeFilters = FILTER_KEYS.filter((key) =>
    Boolean(
      {
        fullName,
        identificationNumber,
        department,
        paygrade,
        location,
        currency,
      }[key]
    )
  ).join(",")

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(1)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current)
        const values: Record<string, string | undefined> = {
          search: debouncedSearch || undefined,
          sortBy,
          sortDirection,
          filters: activeFilters || undefined,
          fullName,
          identificationNumber,
          department,
          paygrade,
          location,
          currency,
          staffCategory,
          groupBy,
          sectionOrder: sectionOrder.join(",") || undefined,
          hiddenSections: hiddenSections.join(",") || undefined,
          excludedElements: serializedExcludedElements,
        }

        Object.entries(values).forEach(([key, value]) => {
          if (value) next.set(key, value)
          else next.delete(key)
        })

        return next.toString() === current.toString() ? current : next
      },
      { replace: true }
    )
  }, [
    debouncedSearch,
    sortBy,
    sortDirection,
    activeFilters,
    fullName,
    identificationNumber,
    department,
    paygrade,
    location,
    currency,
    staffCategory,
    groupBy,
    sectionOrder,
    hiddenSections,
    serializedExcludedElements,
    setSearchParams,
  ])

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "paginatedPayrollReport",
      payrollId,
      preview,
      page,
      pageSize,
      debouncedSearch,
      sortBy,
      sortDirection,
      fullName,
      identificationNumber,
      department,
      paygrade,
      location,
      currency,
      staffCategory,
      groupBy,
      sectionOrder,
      hiddenSections,
      serializedExcludedElements,
    ],
    queryFn: async () => {
      const endpoint = preview
        ? `${basePath}/report_preview/paginated`
        : `${basePath}/report/paginated`
      const response = await newArchAxiosInstance.get(endpoint, {
        params: {
          id: payrollId,
          x: "full",
          pageNumber: page,
          pageSize,
          search: debouncedSearch || undefined,
          sortBy,
          sortDirection,
          fullName,
          identificationNumber,
          department,
          paygrade,
          location,
          currency,
          staffCategory,
          groupBy,
          sectionOrder: sectionOrder.join(",") || undefined,
          hiddenSections: hiddenSections.join(",") || undefined,
          excludedElements: serializedExcludedElements,
        },
      })
      return response.data.data as PayrollReportResponse
    },
    enabled: Boolean(payrollId),
    keepPreviousData: true,
    staleTime: 0,
    onSuccess: () =>
      mixPanelEvent(
        preview
          ? "payroll-report-preview-paginated-listing"
          : "payroll-report-paginated-listing"
      ),
  })

  useEffect(() => {
    const availableSections =
      data?.columnSections
        ?.filter((section) => sectionColorKey(section) !== "EmployeeDetails")
        .map((section) => section.key) || []
    if (availableSections.length === 0) return
    setSectionOrder((current) => [
      ...current.filter((key) => availableSections.includes(key)),
      ...availableSections.filter((key) => !current.includes(key)),
    ])
  }, [data?.columnSections])

  const columns = useMemo<ReportColumn[]>(() => {
    const rows = data?.employees?.docs || []
    const returnedSections =
      view === "compact"
        ? data?.compactColumnSections || data?.columnSections || []
        : data?.columnSections || []
    const sections = returnedSections
      .map((section) => {
        return {
          ...section,
          columns: section.columns
            .filter((key) => key.trim().toLowerCase() !== "currency")
            .filter((key) =>
              rows.some((row) =>
                hasMeaningfulValue(getRowValue(row, key, section.key))
              )
            ),
        }
      })
      .filter((section) => section.columns.length > 0)
    if (view === "detailed") {
      const employeeSection = sections.find(
        (section) => sectionColorKey(section) === "EmployeeDetails"
      )
      const detailedEmployeeColumns = new Set([
        "employee number",
        "full name",
        "department",
        "workplace",
        "paygrade",
      ])
      const employeeColumns = groupBy
        ? employeeSection?.columns || []
        : (employeeSection?.columns || []).filter((key) =>
            detailedEmployeeColumns.has(key.trim().toLowerCase())
          )
      const payrollSections = sections
        .filter((section) => sectionColorKey(section) !== "EmployeeDetails")
        .reduce<ColumnSection[]>((result, section) => {
          if (sectionColorKey(section) === "Summary") {
            const deductions = result.find(
              (item) => sectionColorKey(item) === "Deductions"
            )
            if (deductions) deductions.columns.push(...section.columns)
            return result
          }
          result.push({ ...section, columns: [...section.columns] })
          return result
        }, [])

      return [
        {
          title: groupBy ? "Group" : "Person",
          freezable: true,
          children: [
            {
              title: "Details",
              key: "person.details",
              width: 280,
              render: (_value: unknown, row: DetailedReportRow) => (
                <div className="flex min-w-[240px] flex-col gap-2 py-1">
                  {employeeColumns.map((key) => (
                    <div key={key}>
                      <div className="text-xs font-medium uppercase tracking-wide text-neutral300">
                        {key.replaceAll("_", " ")}
                      </div>
                      <div className="text-sm text-neutral600">
                        {displayReportValue(key, getRowValue(row.person, key))}
                      </div>
                    </div>
                  ))}
                </div>
              ),
            },
          ],
        },
        ...payrollSections.map((section) => ({
          title: section.title,
          freezable: false,
          onHeaderCell: () => ({
            style: {
              backgroundColor: colors[sectionColorKey(section)],
              color: "#FFFFFF",
              textAlign: "left" as const,
            },
          }),
          children: [
            {
              title: (
                <div
                  className={`grid gap-4 ${
                    sectionColorKey(section) === "Earnings"
                      ? "grid-cols-[minmax(180px,1fr)_140px_140px]"
                      : "grid-cols-[minmax(180px,1fr)_140px]"
                  }`}
                >
                  <span>Description</span>
                  <span>Amount</span>
                  {sectionColorKey(section) === "Earnings" && (
                    <span>Tax Amount</span>
                  )}
                </div>
              ),
              key: `${section.key}.items`,
              width: sectionColorKey(section) === "Earnings" ? 520 : 370,
              render: (_value: unknown, row: DetailedReportRow) => {
                const items = row.sections[section.key] || []
                if (!items.length) return "—"
                return (
                  <div className="flex flex-col">
                    {items.map((item, index) => (
                      <div
                        key={`${item.description}-${index}`}
                        className={`grid items-start gap-4 border-b border-neutral40 py-2 last:border-b-0 ${
                          sectionColorKey(section) === "Earnings"
                            ? "grid-cols-[minmax(180px,1fr)_140px_140px]"
                            : "grid-cols-[minmax(180px,1fr)_140px]"
                        } ${item.isTotal ? "font-semibold" : ""}`}
                      >
                        <span>{item.description}</span>
                        <span>{displayValue(item.amount)}</span>
                        {sectionColorKey(section) === "Earnings" && (
                          <span>{displayValue(item.taxAmount)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )
              },
            },
          ],
        })),
      ]
    }

    return sections.map((section, sectionIndex) => {
      const isEmployeeDetails = sectionColorKey(section) === "EmployeeDetails"
      return {
        title: section.title,
        freezable: isEmployeeDetails,
        onHeaderCell: () => ({
          style: {
            backgroundColor: colors[sectionColorKey(section)],
            color: "#FFFFFF",
            textAlign: "left",
          },
        }),
        children: section.columns.map((key, columnIndex) => {
          const normalizedKey = key.trim().toLowerCase()
          const filterConfig:
            | { value?: string; options: string[] }
            | undefined = isEmployeeDetails
            ? {
                "full name": {
                  value: fullName,
                  options: data?.filterOptions?.fullNames || [],
                },
                "employee number": {
                  value: identificationNumber,
                  options: data?.filterOptions?.identificationNumbers || [],
                },
                department: {
                  value: department,
                  options: data?.filterOptions?.departments || [],
                },
                paygrade: {
                  value: paygrade,
                  options: data?.filterOptions?.paygrades || [],
                },
                workplace: {
                  value: location,
                  options: data?.filterOptions?.locations || [],
                },
                currency: {
                  value: currency,
                  options: data?.filterOptions?.currencies || [],
                },
              }[normalizedKey]
            : undefined

          return {
            title: key.endsWith("::tax")
              ? `${key.replace("::tax", "")} Tax`
              : key.replaceAll("_", " "),
            key,
            dataIndex: key,
            sorter: isEmployeeDetails
              ? { multiple: 10000 - sectionIndex * 1000 - columnIndex }
              : undefined,
            sortOrder: !isEmployeeDetails
              ? undefined
              : sorts.find((sort) => sort.field === key)?.direction === "desc"
              ? "descend"
              : sorts.some((sort) => sort.field === key)
              ? "ascend"
              : null,
            filters: filterConfig?.options.map((value) => ({
              text: value,
              value,
            })),
            filteredValue: filterConfig?.value
              ? filterConfig.value.split(",").filter(Boolean)
              : null,
            filterSearch: true,
            ellipsis: true,
            width: Math.max(140, Math.min(240, key.length * 11)),
            onHeaderCell: () => ({
              style: {
                textAlign: "left",
              },
            }),
            render: (_value, row) => {
              const value = getRowValue(row, key, section.key)
              return displayReportValue(key, value)
            },
          }
        }),
      }
    })
  }, [
    colors,
    currency,
    data?.columnSections,
    data?.compactColumnSections,
    data?.employees?.docs,
    data?.filterOptions,
    department,
    fullName,
    staffCategory,
    groupBy,
    identificationNumber,
    location,
    paygrade,
    sorts,
    view,
  ])

  const detailedRows = useMemo(() => {
    if (view !== "detailed") return []
    const payrollSections = (data?.columnSections || []).filter(
      (section) => sectionColorKey(section) !== "EmployeeDetails"
    )

    return (data?.employees?.docs || []).map((person, personIndex) => {
      const itemsBySection = payrollSections.reduce<
        Record<string, DetailedSectionItem[]>
      >((sections, section) => {
        const earningItems = new Map(
          (person.earning_items || []).map((item) => [
            item.description.toLowerCase(),
            item,
          ])
        )
        const items: DetailedSectionItem[] = section.columns
          .filter((description) => !description.endsWith("::tax"))
          .filter((description) => isExpandedViewItem(section, description))
          .filter((description) =>
            Number(getRowValue(person, description, section.key) || 0)
          )
          .map((description) => ({
            description,
            amount:
              sectionColorKey(section) === "Earnings"
                ? earningItems.get(description.toLowerCase())?.amount ??
                  Number(getRowValue(person, description, section.key) || 0)
                : Number(getRowValue(person, description, section.key) || 0),
            taxAmount:
              sectionColorKey(section) === "Earnings"
                ? earningItems.get(description.toLowerCase())?.tax_amount
                : undefined,
          }))
        if (sectionColorKey(section) === "Earnings") {
          items.push({
            description: "Total",
            amount: person.total_amount || 0,
            taxAmount: person.total_tax || 0,
            isTotal: true,
          })
        }
        const targetSection =
          sectionColorKey(section) === "Summary"
            ? payrollSections.find(
                (item) => sectionColorKey(item) === "Deductions"
              )?.key || section.key
            : section.key
        sections[targetSection] = [...(sections[targetSection] || []), ...items]
        return sections
      }, {})

      const personKey =
        person.person_id || person.full_name || person.group || personIndex

      return {
        key: String(personKey),
        person,
        sections: itemsBySection,
      }
    })
  }, [data?.columnSections, data?.employees?.docs, view])

  const summarySections = useMemo<SummarySection[]>(() => {
    if (data?.summary?.sections?.length) return data.summary.sections

    if (data?.summarySections?.length) {
      const sectionsByKey = new Map(
        (data.columnSections || []).map((section) => [section.key, section])
      )

      return data.summarySections
        .map((summarySection) => {
          const columnSection = sectionsByKey.get(summarySection.key)
          if (
            columnSection &&
            sectionColorKey(columnSection) === "Deductions"
          ) {
            const summaryItems = new Map(
              summarySection.items.map((item) => [
                item.description.toLowerCase(),
                item,
              ])
            )

            return {
              ...summarySection,
              items: columnSection.columns
                .map((description) => {
                  const serverItem = summaryItems.get(description.toLowerCase())
                  if (serverItem) return serverItem

                  const amounts = (data.employees?.docs || [])
                    .map((employee) =>
                      getRowValue(employee, description, columnSection.key)
                    )
                    .filter(
                      (amount): amount is number =>
                        typeof amount === "number" && amount !== 0
                    )
                  return {
                    description,
                    peopleCount: amounts.length,
                    totalAmount: amounts.reduce(
                      (total, amount) => total + amount,
                      0
                    ),
                  }
                })
                .filter((item) => item.totalAmount !== 0),
            }
          }

          return {
            ...summarySection,
            items: columnSection
              ? summarySection.items.filter(
                  (item) =>
                    isExpandedViewItem(columnSection, item.description) &&
                    item.totalAmount !== 0
                )
              : summarySection.items.filter((item) => item.totalAmount !== 0),
          }
        })
        .filter((section) => section.items.length > 0)
    }

    const employees = data?.employees?.docs || []
    return (data?.columnSections || [])
      .filter((section) => sectionColorKey(section) !== "EmployeeDetails")
      .map((section) => ({
        key: section.key,
        title: section.title,
        items: section.columns
          .filter((description) => isExpandedViewItem(section, description))
          .map((description) => {
            const amounts = employees
              .map((employee) =>
                getRowValue(employee, description, section.key)
              )
              .filter(
                (amount): amount is number =>
                  typeof amount === "number" && amount !== 0
              )

            return {
              description: description.endsWith("::tax")
                ? `${description.replace("::tax", "")} Tax`
                : description,
              peopleCount: amounts.length,
              totalAmount: amounts.reduce((total, amount) => total + amount, 0),
            }
          })
          .filter((item) => item.totalAmount !== 0),
      }))
      .filter((section) => section.items.length > 0)
  }, [
    data?.columnSections,
    data?.employees?.docs,
    data?.summary?.sections,
    data?.summarySections,
  ])

  const handleTableChange = (
    pagination: TablePaginationConfig,
    tableFilters: Record<string, (React.Key | boolean)[] | null>,
    sorter: SorterResult<PayrollReportRow> | SorterResult<PayrollReportRow>[]
  ) => {
    setPage(pagination.current || 1)
    setPageSize(pagination.pageSize || 20)
    const selectedFilter = (key: string) =>
      tableFilters[key]?.map(String).filter(Boolean).join(",") || undefined
    setFullName(selectedFilter("Full name"))
    setIdentificationNumber(selectedFilter("Employee Number"))
    setDepartment(selectedFilter("Department"))
    setPaygrade(selectedFilter("Paygrade"))
    setLocation(selectedFilter("Workplace"))
    setCurrency(selectedFilter("Currency"))
    const activeSorters = (Array.isArray(sorter) ? sorter : [sorter])
      .filter(
        (
          item
        ): item is SorterResult<PayrollReportRow> & {
          columnKey: React.Key
          order: "ascend" | "descend"
        } => Boolean(item?.columnKey && item?.order)
      )
      .map((item) => ({
        field: String(item.columnKey),
        direction:
          item.order === "descend" ? ("desc" as const) : ("asc" as const),
      }))
    setSorts(activeSorters)
  }

  const downloadReport = async (format: DownloadFormat) => {
    setDownloading(format)
    try {
      const response = await newArchAxiosInstance.get(
        view === "summary"
          ? `${basePath}/report/summary/download`
          : `${basePath}/download_report`,
        {
          params: {
            id: payrollId,
            preview,
            x: "full",
            format,
            "colors.EmployeeDetails": colors.EmployeeDetails,
            "colors.Earnings": colors.Earnings,
            "colors.Deductions": colors.Deductions,
            "colors.Summary": colors.Summary,
            "colors.EmployerContributions": colors.EmployerContributions,
            "colors.InformationAmortization": colors.InformationAmortization,
            "colors.SectionOrder": sectionOrder.join(",") || undefined,
            "colors.HiddenSections": hiddenSections.join(",") || undefined,
            excludedElements: serializedExcludedElements,
            staffCategory,
            groupBy,
            sortBy,
            sortDirection,
            search: debouncedSearch || undefined,
            fullName,
            identificationNumber,
            department,
            paygrade,
            location,
            currency,
          },
          responseType: "blob",
        }
      )
      const disposition = response.headers["content-disposition"] || ""
      const headerName = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(
        disposition
      )?.[1]
      const fallback = `${
        data?.report?.report_download_title || "payroll-report"
      }.${format === "pdf" ? "pdf" : "xlsx"}`
      saveAs(response.data, decodeURIComponent(headerName || fallback))
    } catch {
      message.error("The payroll report could not be downloaded.")
    } finally {
      setDownloading(undefined)
    }
  }

  const colorCustomizer = (
    <div className="w-[280px] space-y-3 p-1">
      <div>
        <div className="font-semibold text-neutral500">Report colors</div>
        <div className="text-xs text-padeSubtext">
          These colors are applied to the table and downloaded report.
        </div>
      </div>
      {(Object.keys(COLOR_LABELS) as Array<keyof SectionColors>).map((key) => (
        <label
          className="flex items-center justify-between gap-3 text-sm"
          key={key}
        >
          <span>{COLOR_LABELS[key]}</span>
          <input
            aria-label={`${COLOR_LABELS[key]} color`}
            className="h-8 w-12 cursor-pointer rounded border border-neutral200 bg-white p-0.5"
            type="color"
            value={colors[key]}
            onChange={(event) =>
              setColors((current) => ({
                ...current,
                [key]: event.target.value,
              }))
            }
          />
        </label>
      ))}
      <AntdButton
        block
        onClick={() => setColors(DEFAULT_COLORS)}
      >
        Reset colors
      </AntdButton>
    </div>
  )

  const configurableSections = (data?.columnSections || []).filter(
    (section) => sectionColorKey(section) !== "EmployeeDetails"
  )
  const orderedConfigurableSections = sectionOrder
    .map((key) => configurableSections.find((section) => section.key === key))
    .filter((section): section is ColumnSection => Boolean(section))

  const moveSection = (key: string, direction: -1 | 1) => {
    setSectionOrder((current) => {
      const next = [...current]
      const index = next.indexOf(key)
      const target = index + direction
      if (index < 0 || target < 0 || target >= next.length) return current
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
  }

  const sectionCustomizer = (
    <div className="w-[320px] space-y-3 p-1">
      <div>
        <div className="font-semibold text-neutral500">Report sections</div>
        <div className="text-xs text-padeSubtext">
          Choose which compact-view sections to show and their order.
        </div>
      </div>
      <div className="space-y-2">
        {orderedConfigurableSections.map((section, index) => (
          <div
            className="flex items-center gap-2 rounded border border-neutral100 px-2 py-2"
            key={section.key}
          >
            <AntdButton
              type="text"
              size="small"
              aria-label={`${
                hiddenSections.includes(section.key) ? "Show" : "Hide"
              } ${section.title}`}
              icon={
                hiddenSections.includes(section.key) ? (
                  <EyeSlashIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                )
              }
              onClick={() =>
                setHiddenSections((current) =>
                  current.includes(section.key)
                    ? current.filter((key) => key !== section.key)
                    : [...current, section.key]
                )
              }
            >
              {hiddenSections.includes(section.key) ? "Hidden" : "Visible"}
            </AntdButton>
            <span className="min-w-0 flex-1 truncate text-sm">
              {section.title}
            </span>
            <AntdButton
              type="text"
              size="small"
              disabled={index === 0}
              icon={<ChevronUpIcon className="h-4 w-4" />}
              onClick={() => moveSection(section.key, -1)}
            />
            <AntdButton
              type="text"
              size="small"
              disabled={index === orderedConfigurableSections.length - 1}
              icon={<ChevronDownIcon className="h-4 w-4" />}
              onClick={() => moveSection(section.key, 1)}
            />
          </div>
        ))}
      </div>
    </div>
  )

  const filterControls = (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        aria-label="Staff category"
        className="min-w-[180px]"
        value={staffCategory || "All"}
        options={staffCategories.options}
        loading={staffCategories.isLoading}
        status={staffCategories.isError ? "error" : undefined}
        onChange={(value) => {
          setStaffCategory(value === "All" ? undefined : value)
          setPage(1)
        }}
      />
      <Input
        allowClear
        className="w-[220px]"
        prefix={<MagnifyingGlassIcon className="h-4 w-4 text-[#62748e]" />}
        placeholder="Search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <Select
        allowClear
        className="min-w-[170px]"
        placeholder="Choose group by"
        value={groupBy}
        options={[
          { label: "Department", value: "department" },
          { label: "Paygrade", value: "paygrade" },
          { label: "Workplace", value: "workplace" },
          { label: "Currency", value: "currency" },
        ]}
        onChange={(value) => {
          setGroupBy(value)
          setPage(1)
        }}
      />
    </div>
  )

  const exportItems = [
    {
      key: "excel",
      label: (
        <button
          disabled={Boolean(downloading)}
          onClick={() => downloadReport("excel")}
          className="w-full text-left font-circular"
        >
          Excel (.xlsx)
        </button>
      ),
    },
    {
      key: "pdf",
      label: (
        <button
          disabled={Boolean(downloading)}
          onClick={() => downloadReport("pdf")}
          className="w-full text-left font-circular"
        >
          PDF
        </button>
      ),
    },
  ]

  return (
    <DashboardContainer>
      <div className="flex h-full w-full flex-col font-circular [&_*]:!font-circular">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-xl font-semibold text-neutral500">
              {data?.report?.payrollStatusId && data.report.payrollStatusId < 5
                ? "Payroll Journal Report (Draft)"
                : "Payroll Journal Report"}
            </h1>
            {data?.report?.report_date ? (
              <p className="mb-0 mt-1 text-sm text-padeSubtext">
                Report created on: {formatReportDate(data.report.report_date)}
              </p>
            ) : isLoading ? (
              <div className="mt-2">
                <Skeleton
                  width="220px"
                  height="14px"
                />
              </div>
            ) : null}
          </div>
          <div className="hidden max-w-full shrink-0 flex-wrap items-center gap-3 md:flex">
            <div
              role="group"
              aria-label="Report view"
              className="flex shrink-0 items-stretch gap-0.5 border-b border-[#f0f0f0]"
            >
              {[
                { key: "compact", label: "Compact", Icon: Squares2X2Icon },
                { key: "detailed", label: "Detailed", Icon: TableCellsIcon },
                { key: "summary", label: "Summary", Icon: null },
              ].map(({ key, label, Icon }) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={view === key}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-t-sm border px-4 py-2 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary500 ${
                    view === key
                      ? "border-[#f0f0f0] border-b-white bg-white text-[#1677ff]"
                      : "border-transparent bg-[#f0f0f0] text-neutral500"
                  }`}
                  onClick={() =>
                    setSearchParams((current) => {
                      const next = new URLSearchParams(current)
                      next.set("format", key)
                      return next
                    })
                  }
                >
                  {Icon && <Icon className="h-5 w-5" />}
                  {label}
                </button>
              ))}
            </div>
            {view === "compact" && (
              <Popover
                content={sectionCustomizer}
                placement="bottomRight"
                trigger="click"
              >
                <AntdButton
                  className="flex items-center"
                  icon={<AdjustmentsHorizontalIcon className="h-4 w-4" />}
                >
                  Arrange sections
                </AntdButton>
              </Popover>
            )}
            {view === "compact" && (
              <Popover
                content={colorCustomizer}
                placement="bottomRight"
                trigger="click"
              >
                <Tooltip title="Customize colors">
                  <AntdButton
                    aria-label="Customize colors"
                    className="flex items-center justify-center"
                    icon={<SwatchIcon className="h-4 w-4" />}
                  />
                </Tooltip>
              </Popover>
            )}
            <ActionsButton
              title={downloading ? "Exporting..." : "Export As"}
              color="primary"
              items={exportItems}
            />
          </div>
        </div>

        <div className="bg-white p-4">
          {view === "summary" && isLoading ? (
            <PayrollJournalSummarySkeleton />
          ) : view === "summary" ? (
            <div className="space-y-6">
              {data?.summary && (
                <div className="grid gap-1 text-sm text-neutral500 md:grid-cols-2">
                  <div>
                    <span className="font-semibold">Name:</span>{" "}
                    {data.summary.name}
                  </div>
                  <div>
                    <span className="font-semibold">Report date:</span>{" "}
                    {data.summary.reportDate}
                  </div>
                  <div className="md:col-span-2">{data.summary.title}</div>
                  {data.summary.employmentType && (
                    <div className="md:col-span-2">
                      <span className="font-semibold">Employment type:</span>{" "}
                      {data.summary.employmentType}
                    </div>
                  )}
                </div>
              )}
              {summarySections.map((section) => {
                const elementOptions = Array.from(
                  new Set([
                    ...(section.availableElements ||
                      section.items.map((item) => item.description)),
                    ...(excludedElements[section.key] || []),
                  ])
                )

                return (
                  <div
                    key={section.key}
                    className="space-y-2"
                  >
                    <EnhancedReportTable
                      title={() => (
                        <div className="flex w-full flex-wrap items-center justify-between gap-3">
                          <h3 className="text-base font-semibold text-neutral700">
                            {section.title}
                          </h3>
                          <div className="flex w-full flex-col items-stretch gap-1 sm:w-auto sm:items-end">
                            <label
                              htmlFor={`exclude-elements-${section.key}`}
                              className="text-sm font-medium text-neutral500"
                            >
                              Elements to exclude
                            </label>
                            <Select
                              id={`exclude-elements-${section.key}`}
                              aria-label={`Exclude elements from ${section.title}`}
                              mode="multiple"
                              allowClear
                              showSearch
                              optionFilterProp="label"
                              maxTagCount={1}
                              placeholder="None excluded"
                              className="w-full min-w-[200px] sm:min-w-[240px]"
                              value={excludedElements[section.key] || []}
                              options={elementOptions.map((element) => ({
                                label: element,
                                value: element,
                              }))}
                              onChange={(elements) =>
                                setExcludedElements((current) => {
                                  const next = { ...current }
                                  if (elements.length)
                                    next[section.key] = elements
                                  else delete next[section.key]
                                  return next
                                })
                              }
                            />
                          </div>
                        </div>
                      )}
                      columns={[
                        {
                          title: "Element Name",
                          dataIndex: "description",
                          key: "description",
                        },
                        {
                          title: "Amount",
                          dataIndex: "totalAmount",
                          key: "totalAmount",
                          render: (value: number) => displayValue(value),
                        },
                        {
                          title: "Count",
                          dataIndex: "peopleCount",
                          key: "peopleCount",
                        },
                      ]}
                      dataSource={section.items}
                      rowKey="description"
                      pagination={false}
                      bordered
                    />
                    {(section.currencyTotals?.length
                      ? section.currencyTotals
                      : [{ currency: "", totalAmount: section.totalAmount }]
                    ).map((total) => (
                      <div
                        key={total.currency || section.key}
                        className="flex justify-end gap-8 border border-neutral200 px-4 py-2 font-semibold text-neutral500"
                      >
                        <span>
                          Total {section.title}
                          {total.currency ? ` (${total.currency})` : ""}
                        </span>
                        <span>{displayValue(total.totalAmount)}</span>
                      </div>
                    ))}
                  </div>
                )
              })}
              {data?.summary &&
                (data.summary.currencyTotals?.length
                  ? data.summary.currencyTotals
                  : [{ currency: "", netTotal: data.summary.netTotal }]
                ).map((total) => (
                  <div
                    key={total.currency || "net-total"}
                    className="flex justify-end gap-8 border border-neutral200 px-4 py-3 text-base font-semibold text-neutral500"
                  >
                    <span>
                      Net Total{total.currency ? ` (${total.currency})` : ""}
                    </span>
                    <span>{displayValue(total.netTotal)}</span>
                  </div>
                ))}
              {!isLoading && !isFetching && summarySections.length === 0 && (
                <div className="py-12 text-center text-neutral300">
                  No payroll summary is available for the selected records.
                </div>
              )}
            </div>
          ) : isLoading ? (
            <PayrollJournalSkeleton
              bands={[
                view === "compact" ? colors.EmployeeDetails : "#dfe2e6",
                colors.Earnings,
                colors.Deductions,
                colors.Summary,
              ]}
            />
          ) : (
            <EnhancedReportTable<any>
              className="payroll-report-table"
              columns={columns}
              dataSource={
                view === "detailed" ? detailedRows : data?.employees?.docs || []
              }
              loading={isLoading || isFetching}
              defaultFreezeBoundaryKey={
                columns[0]?.children?.[0]?.key as string
              }
              freezeGroupedColumnsByLeaf
              serialNumberOffset={
                ((data?.employees?.page || page) - 1) *
                (data?.employees?.limit || pageSize)
              }
              toolbarLeading={filterControls}
              onChange={handleTableChange}
              pagination={{
                current: data?.employees?.page || page,
                pageSize: data?.employees?.limit || pageSize,
                total: data?.employees?.totalDocs || 0,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100, 200],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total}`,
              }}
              scroll={{ x: "max-content", y: "calc(100vh - 300px)" }}
              tableLayout="auto"
              rowKey={(row) =>
                view === "detailed"
                  ? (row as DetailedReportRow).key
                  : (row as PayrollReportRow).person_id ||
                    (row as PayrollReportRow).full_name ||
                    (row as PayrollReportRow).group ||
                    ""
              }
              bordered
            />
          )}
        </div>
      </div>
    </DashboardContainer>
  )
}

export default PaginatedPayrollReportDetails

export const getHireDateTime = (hireDate?: string) => {
  if (!hireDate) return Number.MAX_SAFE_INTEGER
  const [day, month, year] = hireDate.split("/").map(Number)
  if (!day || !month || !year) return Number.MAX_SAFE_INTEGER
  return new Date(year, month - 1, day).getTime()
}
